import React from 'react';

import { Box, ScrollView } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { NutritionInformation } from 'components/product-detail-v2/nutrition-information';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductUpsell } from 'components/product-detail-v2/product-upsell';
import { transformProductWizardStepToEventAttr } from 'components/product-detail-v2/utils';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { primitive } from 'styles/constants/primitives';
import { isCombo } from 'utils/menu';

import { ProductDetailHeader } from '../product-detail-header';

import { OrderSummaryPanel } from './order-summary-panel';
import {
  ProductOrderReviewContainer,
  ProductOrderReviewInnerLayout,
  ProductOrderReviewWrapper,
  ProductReviewLayoutContainer,
} from './styled';
import { IProductOrderReview } from './types';

export const ProductOrderReview: React.FC<IProductOrderReview> = ({ modifierSelections }) => {
  const { formatMessage } = useIntl();
  const { goBack } = useNavigation();
  const {
    currentStep,
    goToHome,
    goToCustomizeProduct,
    isProductReviewFromCart,
  } = useProductWizardFlow();
  const {
    menuObject,
    productWizardFlowSteps,
    resetProductWizard,
    setIsBackNavigation,
    selectedProduct,
    userSelections,
  } = useProductWizardContext();

  const isMobile = useIsMobileBreakpoint();

  const foundFirstStep = productWizardFlowSteps.find(step => !!step.isFirstStep);
  const shouldRedirectToHome = !foundFirstStep && !isProductReviewFromCart;

  React.useEffect(() => {
    if (shouldRedirectToHome) {
      resetProductWizard();
      goToHome();
    }
  }, [goToHome, resetProductWizard, shouldRedirectToHome]);

  const lastScreen = productWizardFlowSteps.at(-1);

  const handleBackNavigation = React.useCallback(() => {
    setIsBackNavigation(true);

    if (isProductReviewFromCart) {
      return goBack();
    }

    return goToCustomizeProduct({
      slotKey: lastScreen?.id ?? '',
      step: ProductWizardStep.ProductCustomization,
    });
  }, [goBack, goToCustomizeProduct, isProductReviewFromCart, lastScreen?.id, setIsBackNavigation]);

  const productDetailHeaderTitle = formatMessage({
    id: isCombo(selectedProduct) ? 'reviewYourMeal' : 'reviewYourSelection',
  });

  const backNavigationLabel =
    (lastScreen && userSelections.comboSlot?.get(lastScreen.id)?.selectedItem?.name.locale) ?? '';

  if (shouldRedirectToHome) {
    // redirecting don't display janky UI
    return null;
  }

  return (
    <ProductOrderReviewWrapper>
      <ProductDetailHeader
        title={productDetailHeaderTitle}
        currentStep={currentStep}
        onPress={handleBackNavigation}
        backgroundColor={primitive.$white}
        backNavigationLabel={formatMessage(
          { id: 'productDetailPickerCustomizationGoBack' },
          { screen: backNavigationLabel }
        )}
        eventAttributes={{ name: transformProductWizardStepToEventAttr(currentStep) }}
      />

      <ScrollView showsVerticalScrollIndicator>
        <Box width={'full'}>
          <ProductReviewLayoutContainer isFullContainer={isMobile} background={primitive.$white}>
            <ProductOrderReviewInnerLayout>
              <ProductOrderReviewInnerLayout>
                <OrderSummaryPanel />
              </ProductOrderReviewInnerLayout>
            </ProductOrderReviewInnerLayout>
          </ProductReviewLayoutContainer>
        </Box>

        <ProductReviewLayoutContainer isFullContainer={isMobile}>
          <ProductOrderReviewInnerLayout>
            <ProductOrderReviewContainer>
              <ProductUpsell />
              <NutritionInformation />
            </ProductOrderReviewContainer>
          </ProductOrderReviewInnerLayout>
        </ProductReviewLayoutContainer>
      </ScrollView>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
      />
    </ProductOrderReviewWrapper>
  );
};
